import React, { useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Icon from "../Icon/Icon";
import useCheckRecordStatusAndTypes from "hooks/useCheckRecordStatusAndTypes";
import "./Cards.scss";
import "../../blocks/CommsList/CommsList.scss";

interface Props {
  arrData2: any[];
}

const getBadgeVariant = (estado: number): string => {
  if (estado === 0)  return "warn";
  if (estado === 3)  return "success";
  if (estado === -1) return "muted";
  return "default";
};

const formatSpanishDate = (value: string | Date): string => {
  const date = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return new Intl.DateTimeFormat("es-ES", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
};

const navigatePath = (tipo: number, idPrestacion: string, idForm: string): string => {
  switch (tipo) {
    case 1:   return `/solicitud-volante-asistencia/${idPrestacion}/${idForm}`;
    case 2:   return `/gastos-contingencia-profesional/${idPrestacion}/${idForm}`;
    case 3:   return `/formulario-certificado-empresa/${idPrestacion}/${idForm}`;
    case 4:   return `/declaracion-situacion-actividad/${idPrestacion}/${idForm}`;
    case 5:   return `/solicitud-pago-directo-incapacidad-temporal/${idPrestacion}/${idForm}`;
    case 7:   return `/formulario-reconocimiento-rel/${idPrestacion}/${idForm}`;
    case 9:   return `/formulario-prestacion-economica-rel/${idPrestacion}/${idForm}`;
    case 12:  return `/formulario-pecata/${idPrestacion}/${idForm}`;
    case 13:  return `/pago-unico-cata/${idPrestacion}/${idForm}`;
    case 14:  return `/formulario-reanudacion-cata/${idPrestacion}/${idForm}`;
    case 15:  return `/declaracion-cese-trade/${idPrestacion}/${idForm}`;
    case 16:  return `/declaracion-jurada-cese-actividad-directa/${idPrestacion}/${idForm}`;
    case 17:  return `/declaracion-jurada-cese-actividad-objetiva/${idPrestacion}/${idForm}`;
    case 18:  return `/declaracion-jurada-cese-actividad-parcial/${idPrestacion}/${idForm}`;
    case 19:  return `/declaracion-jurada-garantias-laborales/${idPrestacion}/${idForm}`;
    case 20:  return `/declaracion-jurada-violencia-genero/${idPrestacion}/${idForm}`;
    case 21:  return `/prestacion-modalidad-ciclica/${idPrestacion}/${idForm}`;
    case 22:  return `/prestacion-modalidad-sectorial/${idPrestacion}/${idForm}`;
    case 23:  return `/prestacion-cume/${idPrestacion}/${idForm}`;
    case 26:  return `/trabajador-cuenta-propia/${idPrestacion}/${idForm}`;
    case 28:  return `/prestaciones-especiales/${idPrestacion}/${idForm}`;
    case 29:  return `/solicitud-modelo145/${idPrestacion}/${idForm}`;
    case 33:  return `/prestacion-empleados-hogar/${idPrestacion}/${idForm}`;
    case 34:  return `/formulario-cata-fuego/${idPrestacion}/${idForm}`;
    case 35:  return `/solicitud-gastos-farmacia/${idPrestacion}/${idForm}`;
    case 36:  return `/solicitud-gastos-ortopedia/${idPrestacion}/${idForm}`;
    case 37:  return `/solicitud-gastos-transporte/${idPrestacion}/${idForm}`;
    case 22021:
    case 112021:
    case 182021: return `/formulario-real-decreto/${idPrestacion}/${idForm}`;
    default:  return `/view404`;
  }
};

const PAGE_SIZE = 5;

const RoyalDecreeFormsCards: React.FC<Props> = ({ arrData2 }) => {
  const params = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { checkFormStatusRD } = useCheckRecordStatusAndTypes();
  const [activeTab, setActiveTab] = useState<string>("");
  const [page, setPage] = useState(1);

  const enriched = useMemo(() =>
    (arrData2 || []).map((form) => ({
      ...form,
      EstadoGeneralRevision: checkFormStatusRD(form.EstadoGeneralRevision),
      _variant: getBadgeVariant(form.Estado),
    })),
    [arrData2]
  );

  // unique states sorted by Estado number
  const uniqueStates = useMemo(() => {
    const seen = new Map<string, number>();
    enriched.forEach((f) => {
      if (!seen.has(f.EstadoDescripcion)) seen.set(f.EstadoDescripcion, f.Estado);
    });
    return Array.from(seen.entries())
      .map(([label, estado]) => ({ label, estado }))
      .sort((a, b) => a.estado - b.estado);
  }, [enriched]);

  const defaultTab = uniqueStates[0]?.label ?? "";

  const filtered = useMemo(() => {
    const tab = activeTab || defaultTab;
    return enriched.filter((f) => f.EstadoDescripcion === tab);
  }, [enriched, activeTab, defaultTab]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage   = Math.min(page, totalPages);
  const paged      = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const getCount = (label: string) =>
    enriched.filter((f) => f.EstadoDescripcion === label).length;

  const handleTabChange = (label: string) => {
    setActiveTab(label);
    setPage(1);
  };

  if (!enriched.length) {
    return <p className="cards-empty">No hay formularios para mostrar.</p>;
  }

  return (
    <div>
      <div className="forms-pills">
        {uniqueStates.map(({ label, estado }) => (
          <button
            key={label}
            type="button"
            className={`forms-pill${(activeTab || defaultTab) === label ? " forms-pill--active" : ""}`}
            onClick={() => handleTabChange(label)}
          >
            {label}
            <span className="forms-pill__count">{getCount(label)}</span>
          </button>
        ))}
      </div>

      <div className="cards-list">
        {paged.map((item: any, i: number) => (
          <article
            key={`${item.Id}-${i}`}
            className="item-card item-card--compact item-card--4col item-card--clickable"
            role="button"
            tabIndex={0}
            onClick={() => navigate(navigatePath(item.Tipo, params.id, item.Id))}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                navigate(navigatePath(item.Tipo, params.id, item.Id));
              }
            }}
          >
            <div className="item-card__icon item-card__icon--purple">
              <Icon name="file-text" size={18} />
            </div>
            <div className="item-card__content">
              <div className="item-card__title">{item.TipoDescripcion}</div>
            </div>
            <div className="item-card__badge">
              {item.EstadoDescripcion && (
                <span className="item-card__sub">{item.EstadoDescripcion}</span>
              )}
            </div>
            <div className="item-card__action">
              {item.Fecha && (
                <span className="item-card__sub" style={{ whiteSpace: "nowrap" }}>
                  {formatSpanishDate(item.Fecha)}
                </span>
              )}
            </div>
          </article>
        ))}
      </div>

      {totalPages > 1 && (
        <div className="comms-list__pager">
          <span className="comms-list__pager-info">
            {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, filtered.length)} de {filtered.length}
          </span>
          <div className="comms-list__pager-btns">
            <button
              className="btn btn--ghost btn--sm"
              disabled={safePage <= 1}
              onClick={() => setPage(safePage - 1)}
            >
              <Icon name="arrow-left" size={16} /> Anterior
            </button>
            <button
              className="btn btn--ghost btn--sm"
              disabled={safePage >= totalPages}
              onClick={() => setPage(safePage + 1)}
            >
              Siguiente <Icon name="arrow-right" size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoyalDecreeFormsCards;
